import React from 'react';
import Icon from '../Icon';

function W({ text, offset = 0 }) {
  return text.split(' ').map((word, i) => (
    <span
      key={`${offset}-${i}`}
      className="hl-word"
      style={{ animationDelay: `${(offset + i) * 90}ms` }}
    >
      {word}{' '}
    </span>
  ));
}

const HEADLINES = {
  deserve: {
    jsx: (
      <>
        <W text="You're already caring for them." offset={0} />
        <em className="hl-em">
          <W text="You deserve to get paid for it." offset={6} />
        </em>
      </>
    ),
  },
  paid: {
    jsx: (
      <>
        <W text="You're caring for them every day." offset={0} />
        <em className="hl-em">
          <W text="You may already qualify to be paid." offset={7} />
        </em>
      </>
    ),
  },
  miss: {
    jsx: (
      <>
        <W text="Most families are leaving" offset={0} />
        <em className="hl-em">
          <W text="$2,000–$4,000 a month" offset={5} />
        </em>
        <W text="on the table." offset={9} />
      </>
    ),
  },
};

const CTA_COPY = {
  default: 'Check Your Eligibility',
  paid:    'See if I can get paid',
  free:    "See if I qualify — it's free",
};

const Hero = ({ onCTA, showImagery = true, headlineStyle = 'deserve', ctaCopy = 'default' }) => (
  <section className="hero" id="top">
    {/* Animated premium background */}
    <div className="hero-bg" aria-hidden="true">
      <div className="hero-bg-mesh" />
      <div className="hero-bg-blob hero-bg-blob-1" />
      <div className="hero-bg-blob hero-bg-blob-2" />
      <div className="hero-bg-blob hero-bg-blob-3" />
      <div className="hero-bg-grid" />
      <div className="hero-bg-shine" />
    </div>
    <div className="container">
      <div className="hero-grid">
        <div className="hero-copy">
          <span className="eyebrow reveal"><Icon name="sparkle" size={12} stroke={2.5} /> Paid Family Caregiving</span>
          <h1 className="display reveal delay-1">{HEADLINES[headlineStyle]?.jsx || HEADLINES.deserve.jsx}</h1>
          <p className="lead reveal delay-2">
            Taking care of a loved one is hard. Most families don't realize they may
            qualify for <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>$2,000–$4,000 / month</strong> through
            government caregiving programs. We help you find out and handle the process for you.
          </p>
          <div className="hero-actions reveal delay-3">
            <button className="btn btn-primary btn-lg" onClick={onCTA}>
              {CTA_COPY[ctaCopy] || CTA_COPY.default}
              <Icon name="arrow-right" size={17} stroke={2.4} className="arrow" />
            </button>
            <a href="#how" className="btn btn-ghost btn-lg" style={{ height: 60, fontSize: 16 }}>
              How it works
            </a>
          </div>
          <div className="cta-meta">
            <span className="cta-meta-item"><Icon name="lock" size={14} stroke={2.2} /> Free &amp; confidential</span>
            <span className="cta-meta-item"><Icon name="clock" size={14} stroke={2.2} /> Takes 2 minutes</span>
            <span className="cta-meta-item"><Icon name="shield" size={14} stroke={2.2} /> No obligation</span>
          </div>
          <div className="trust-row reveal delay-4">
            <div className="avatar-stack">
              <div className="av">MR</div>
              <div className="av">JT</div>
              <div className="av">AL</div>
              <div className="av">+</div>
            </div>
            <div className="trust-text">
              <div className="stars">★★★★★</div>
              <div><strong>12,400+ families</strong> have checked their eligibility this year</div>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          {showImagery && <>
            {/* Soft orbiting glow ring */}
            <div className="hv-glow" aria-hidden="true" />
            <div className="hv-glow hv-glow-2" aria-hidden="true" />

            <div className="photo">
              <img src={process.env.PUBLIC_URL + '/assets/hero-family.png'} alt="Family caregivers" className="photo-img" />
            </div>

            {/* Existing stat cards — refactored as float chips */}
            <div className="float-card float-tl">
              <div className="ic"><Icon name="dollar" size={18} stroke={2.4} /></div>
              <div>
                <div className="value">$3,240/mo</div>
                <div className="label">Avg. monthly payment</div>
              </div>
            </div>
            <div className="float-card float-br">
              <div className="ic blue"><Icon name="check-bold" size={16} /></div>
              <div>
                <div className="value">Approved in 14 days</div>
                <div className="label">Median processing time</div>
              </div>
            </div>

            {/* Floating value-prop chips that loop in/out */}
            <div className="hv-chips" aria-hidden="true">
              <span className="hv-chip hv-chip-1">
                <span className="hv-dot" /> $2,000–$4,000 / month
              </span>
              <span className="hv-chip hv-chip-2">
                <Icon name="heart" size={12} stroke={2.4}/> Get paid for caregiving
              </span>
              <span className="hv-chip hv-chip-3">
                <Icon name="clock" size={12} stroke={2.4}/> Check your eligibility
              </span>
              <span className="hv-chip hv-chip-4">
                <Icon name="shield" size={12} stroke={2.2}/> All 50 states covered
              </span>
            </div>

            {/* Rotating value words */}
            <div className="hv-rotator" aria-hidden="true">
              <span className="hv-rotator-static">For families who</span>
              <span className="hv-rotator-words">
                <span>care deeply.</span>
                <span>show up daily.</span>
                <span>deserve support.</span>
                <span>love completely.</span>
              </span>
            </div>
          </>}
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
